import {
	Button,
	ConfirmationDialog,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@medme/ui"
import { Loader2 } from "lucide-react"
import { useId } from "react"
import { useTranslation } from "react-i18next"
import type { Lead } from "@/shared/api/generated/types/marketing"
import {
	isLeadStatus,
	LEAD_STATUS_KEYS,
	LEAD_UTM_FIELDS,
} from "../model/constants"
import {
	formatLeadDate,
	formatLeadPhone,
	formatLeadSource,
	formatLeadUtmShort,
} from "../model/formatters"
import { useLeadDetails } from "../model/use-lead-details"

type LeadDetailsDialogProps = {
	lead: Lead
	canEdit: boolean
	onClose: () => void
}

export function LeadDetailsDialog({
	lead,
	canEdit,
	onClose,
}: LeadDetailsDialogProps) {
	const { t, i18n } = useTranslation()
	const form = useLeadDetails(lead, onClose)
	const statusId = useId()
	const commentId = useId()
	const commentCountId = useId()

	return (
		<Dialog open onOpenChange={(open) => !open && form.requestClose()}>
			<DialogContent
				className="top-4 right-4 bottom-4 left-4 max-h-[calc(100dvh-2rem)] w-auto max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto p-0 sm:inset-x-auto sm:top-[50%] sm:bottom-auto sm:left-1/2 sm:w-4/5 sm:max-w-none sm:translate-x-[-50%] sm:translate-y-[-50%] md:w-2/3 xl:w-1/2 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:bg-mm-muted-2 [&_[data-slot=dialog-close]]:p-2 [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:hover:bg-mm-border"
				closeLabel={t("leads.actions.close")}
				onEscapeKeyDown={(event) => {
					if (!form.isDirty || form.isDiscardConfirmationOpen) return
					event.preventDefault()
					form.requestClose()
				}}
			>
				<DialogHeader className="shrink-0 border-b px-5 py-5 pe-16 text-start sm:px-6">
					<DialogTitle className="text-xl">
						{t("leads.details.title")}
					</DialogTitle>
					<DialogDescription className="text-base">
						{formatLeadPhone(lead.phone, lead.phoneNormalized)}
					</DialogDescription>
				</DialogHeader>
				<div className="px-5 py-5 sm:px-6">
					<dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
						<div>
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.source")}
							</dt>
							<dd className="mt-1 break-words">
								{formatLeadSource(lead.source)}
							</dd>
						</div>
						<div>
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.domain")}
							</dt>
							<dd className="mt-1 break-all">{lead.domain}</dd>
						</div>
						<div>
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.createdAt")}
							</dt>
							<dd className="mt-1">
								{formatLeadDate(lead.createdAt, i18n.language)}
							</dd>
						</div>
						<div>
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.utm")}
							</dt>
							<dd className="mt-1 break-words">{formatLeadUtmShort(lead)}</dd>
						</div>
						<div className="sm:col-span-2">
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.pageUrl")}
							</dt>
							<dd className="mt-1 break-all">{lead.pageUrl}</dd>
						</div>
						<div className="rounded-xl bg-mm-muted-2 p-3 sm:col-span-2">
							<dt className="text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg">
								{t("leads.fields.utm")}
							</dt>
							<dd className="mt-2">
								<dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
									{LEAD_UTM_FIELDS.map((field) => (
										<div key={field}>
											<dt className="text-xs font-medium">utm_{field}</dt>
											<dd className="break-all text-sm text-mm-muted-fg">
												{lead.utm[field] || "—"}
											</dd>
										</div>
									))}
								</dl>
							</dd>
						</div>
					</dl>
					<div className="mt-5 grid gap-1.5">
						<label className="text-sm font-medium" htmlFor={statusId}>
							{t("leads.fields.status")}
						</label>
						<select
							id={statusId}
							className="w-full rounded-[10px] border border-mm-border bg-mm-muted-2 px-3 py-2.5 outline-none focus-visible:border-mm-primary focus-visible:ring-2 focus-visible:ring-mm-primary/20"
							value={form.status}
							disabled={!canEdit}
							onChange={(event) => {
								const value = event.target.value
								if (isLeadStatus(value)) form.setStatus(value)
							}}
						>
							{Object.entries(LEAD_STATUS_KEYS).map(([value, key]) => (
								<option key={value} value={value}>
									{t(key)}
								</option>
							))}
						</select>
					</div>
					<div className="mt-4 grid gap-1.5">
						<label className="text-sm font-medium" htmlFor={commentId}>
							{t("leads.fields.comment")}
						</label>
						<textarea
							id={commentId}
							aria-describedby={commentCountId}
							className="w-full resize-y rounded-[10px] border border-mm-border bg-mm-muted-2 p-3 outline-none focus-visible:border-mm-primary focus-visible:ring-2 focus-visible:ring-mm-primary/20 disabled:opacity-60"
							maxLength={2000}
							placeholder={t("leads.details.commentPlaceholder")}
							rows={4}
							value={form.comment}
							disabled={!canEdit}
							onChange={(event) => form.setComment(event.target.value)}
						/>
						<span
							id={commentCountId}
							className="text-end text-xs text-mm-muted-fg"
						>
							{t("leads.details.commentCount", {
								count: form.comment.length,
								max: 2000,
							})}
						</span>
					</div>
				</div>
				<DialogFooter className="shrink-0 border-t bg-mm-muted-2 px-5 py-4 sm:px-6">
					<Button
						className="h-auto rounded-[10px] py-2.5"
						variant="outline"
						onClick={form.requestClose}
					>
						{t("leads.actions.cancel")}
					</Button>
					{canEdit && (
						<Button
							className="h-auto rounded-[10px] bg-black py-2.5 text-white hover:bg-black/85"
							disabled={!form.isDirty || form.isSaving}
							onClick={form.save}
						>
							{form.isSaving && <Loader2 className="animate-spin" />}
							{t("leads.actions.save")}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
			<ConfirmationDialog
				open={form.isDiscardConfirmationOpen}
				title={t("leads.details.unsavedChangesTitle")}
				description={t("leads.details.unsavedChangesConfirm")}
				cancelLabel={t("leads.actions.keepEditing")}
				confirmLabel={t("leads.actions.discardChanges")}
				confirmVariant="destructive"
				onCancel={form.keepEditing}
				onConfirm={form.discardChanges}
			/>
		</Dialog>
	)
}
